<template>
  <section
    class="px-4 py-14 sm:px-6 lg:px-8"
    :class="variant === 'inline' ? '' : 'rounded-2xl border border-line bg-surface'"
  >
    <div class="mx-auto max-w-3xl text-center">
      <h2 v-if="heading" class="text-3xl font-semibold tracking-[-0.02em] text-ink md:text-4xl">{{ heading }}</h2>
      <p v-if="subheading" class="mx-auto mt-3 max-w-2xl text-[17px] text-ink-2">{{ subheading }}</p>

      <div class="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
        <router-link
          :to="primary.to"
          class="inline-flex h-12 items-center justify-center rounded-xl bg-accent px-6 text-[15px] font-semibold text-white transition-colors hover:bg-accent-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          :data-cta="primary.eventName || 'cta_primary'"
          @click="onCtaClick(primary)"
        >
          {{ primary.label }}
        </router-link>
        <router-link
          v-if="secondary"
          :to="secondary.to"
          class="inline-flex h-12 items-center justify-center rounded-xl border border-control bg-surface px-6 text-[15px] font-semibold text-ink transition-colors hover:bg-wash focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          :data-cta="secondary.eventName || 'cta_secondary'"
          @click="onCtaClick(secondary)"
        >
          {{ secondary.label }}
        </router-link>
      </div>

      <p v-if="footnote" class="mt-5 text-sm text-ink-4">{{ footnote }}</p>
    </div>
  </section>
</template>


<script setup lang="ts">
import { useAnalytics } from '@/composables/useAnalytics'

export interface CtaItem {
  label: string
  to: string
  eventName?: string
}

defineProps<{
  heading?: string
  subheading?: string
  primary: CtaItem
  secondary?: CtaItem
  footnote?: string
  variant?: 'inline' | 'banner'
}>()

const analytics = useAnalytics()

function onCtaClick(cta: CtaItem) {
  // Map event names → typed analytics calls. Falls through to a generic capture
  // so adding new CTAs does not require touching this map.
  switch (cta.eventName) {
    case 'cta_demo':
      analytics.trackDemoStart({ source: 'cta_block', label: cta.label })
      break
    case 'cta_trial':
    case 'cta_signup':
      analytics.trackSignupStart('cta_block')
      break
    default:
      // Custom event — surface to PostHog as cta_clicked with the label.
      if (cta.eventName) {
        ;(analytics as unknown as { trackPageView: (path: string, props?: Record<string, unknown>) => void })
          .trackPageView(`#cta:${cta.eventName}`, { label: cta.label })
      }
  }
}
</script>
